/**
 * install401Redirect — global "session expired" guard.
 *
 * After the initial AuthGuard check passes, a later 401 (e.g. the cookie
 * expired or was cleared mid-session) previously left the user on a stale
 * shell with silent failures. This patches window.fetch (and, if provided,
 * an axios instance) so ANY 401 from the API bounces the user to the login
 * page — preserving the current URL via ?next= so they land back here.
 *
 * REMINDER: DO NOT HARDCODE THE URL. `loginBase` is injected by the caller
 * from env (idBase for finops/studio; '' for the id app which owns /login).
 *
 * Guards against redirect loops:
 *   - never redirects while already on a /login or /signup route
 *   - never redirects for the auth-probe endpoints themselves
 *     (/auth/me, /auth/password-login, /auth/logout) — their callers own them
 *   - redirects at most once
 */
export function install401Redirect(opts: { loginBase: string; axios?: any }) {
  const { loginBase, axios } = opts;
  let redirecting = false;

  const isAuthPath = () => /\/(login|signup|register)(\?|$|\/)/.test(window.location.pathname + window.location.search);
  const isExemptUrl = (url: string) =>
    /\/api\/auth\/(me|password-login|logout|google|device)/.test(url || '');

  const go = () => {
    if (redirecting || isAuthPath()) return;
    redirecting = true;
    const next = encodeURIComponent(window.location.href);
    window.location.href = `${loginBase}/login?next=${next}`;
  };

  // --- fetch ---
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
    const res = await nativeFetch(input, { credentials: 'include', ...init });
    const url = typeof input === 'string' ? input : (input as Request)?.url || String(input);
    if (res.status === 401 && !isExemptUrl(url)) go();
    return res;
  };

  // --- axios (optional) ---
  if (axios?.interceptors?.response) {
    axios.defaults.withCredentials = true;
    axios.interceptors.response.use(
      (r: any) => r,
      (err: any) => {
        const status = err?.response?.status;
        const url = err?.config?.url || '';
        if (status === 401 && !isExemptUrl(url)) go();
        return Promise.reject(err);
      },
    );
  }
}
